import type { ReportFilter, SectionBuild, SectionDropdown, SectionTopTab } from '@/features/reports'

// Build functions + request-data maps for the SearchAdsExcellence layout (kept beside the
// layout in `searchAdsExcellence.layout.ts`; split only for the 400-line file gate).
// v3 `configs/MarketingInsights/SearchMarketing/SearchAdsExcellence.js` (697 ln) — the largest
// layout, and the reference port for the hard features: topTabs with per-tab request data
// (kept as local maps keyed by machine name), dropdown option gating, checkShow, chart-only
// sections, and `pending` markers for the v3 renderers that have not landed in v4
// (dashboard chart area → module 09; n-gram / bespoke components → follow-up slices).
// Mapping notes beyond the standard table: `scatterJitter` normalizes to `scatter` (the v4
// renderable set), `fieldValues` role hints drop (v4 derives chart field roles), and v3
// `hideCols` shaping waits for ORP-18643's column services.

const BRANDED_OPTIONS: SectionDropdown['options'] = [
  { labelKey: 'reports.searchAdsExcellence.branded.all' },
  { labelKey: 'reports.searchAdsExcellence.branded.nonBranded', value: 0 },
  { labelKey: 'reports.searchAdsExcellence.branded.branded', value: 1 },
]

export const BRANDED_KEYWORD_DROPDOWNS: SectionDropdown[] = [
  {
    filter: { field: 'is_branded_keyword', matchMode: 'equals' },
    labelKey: 'reports.searchAdsExcellence.dropdowns.branded',
    options: BRANDED_OPTIONS,
  },
]

export const BRANDED_CAMPAIGN_DROPDOWNS: SectionDropdown[] = [
  {
    filter: { field: 'is_branded_campaign', matchMode: 'equals' },
    labelKey: 'reports.searchAdsExcellence.dropdowns.branded',
    options: BRANDED_OPTIONS,
  },
]

export const BRANDED_SEARCHTERM_DROPDOWNS: SectionDropdown[] = [
  {
    filter: { field: 'is_branded_searchterm', matchMode: 'equals' },
    labelKey: 'reports.searchAdsExcellence.dropdowns.branded',
    options: [
      { labelKey: 'reports.searchAdsExcellence.branded.allKeywords', reportId: 5 },
      { labelKey: 'reports.searchAdsExcellence.branded.nonBranded', reportId: 5, value: 0 },
      { labelKey: 'reports.searchAdsExcellence.branded.branded', reportId: 5, value: 1 },
    ],
  },
]

export const ORGANIC_BRANDED_DROPDOWN: SectionDropdown = {
  filter: { field: 'is_branded_searchterm', matchMode: 'equals' },
  labelKey: 'reports.searchAdsExcellence.dropdowns.branded',
  options: [
    {
      labelKey: 'reports.searchAdsExcellence.branded.nonBranded',
      value: 0,
      reportId: 38,
      tabs: ['False_branded_keyword'],
    },
    {
      labelKey: 'reports.searchAdsExcellence.branded.branded',
      value: 1,
      reportId: 38,
      tabs: ['True_branded_keyword'],
    },
  ],
}

export const ORGANIC_POSITION_DROPDOWN: SectionDropdown = {
  filter: { field: 'organic_avg_position', matchMode: 'between' },
  labelKey: 'reports.searchAdsExcellence.dropdowns.position',
  options: [
    { labelKey: 'reports.searchAdsExcellence.positions.p1to3', value: [0, 3] },
    { labelKey: 'reports.searchAdsExcellence.positions.p4to10', value: [4, 10] },
    { labelKey: 'reports.searchAdsExcellence.positions.p11to20', value: [11, 20] },
    { labelKey: 'reports.searchAdsExcellence.positions.p20to50', value: [20, 50] },
    { labelKey: 'reports.searchAdsExcellence.positions.p50to100', value: [50, 100] },
  ],
}

// ── Keyword Performance topTabs (v3 per-topTab request data as a local map) ────────────
export const KEYWORD_PERFORMANCE_TOP_TABS: SectionTopTab[] = [
  { name: 'All', labelKey: 'reports.searchAdsExcellence.topTabs.all' },
  { name: 'High Performers', labelKey: 'reports.searchAdsExcellence.topTabs.highPerformers' },
  { name: 'High Potentials', labelKey: 'reports.searchAdsExcellence.topTabs.highPotentials' },
]

export const KEYWORD_PERFORMANCE_TOP_TAB_DATA: Record<
  string,
  { sortField?: string; filters: ReportFilter[] }
> = {
  All: { filters: [] },
  'High Performers': {
    sortField: 'roas',
    filters: [{ field: 'conversions', match_mode: 'greater_than', value: 5 }],
  },
  'High Potentials': {
    sortField: 'roas',
    filters: [
      { field: 'lost_topimpressionshare_budget', match_mode: 'greater_than', value: 0.2 },
      { field: 'search_impression_share', match_mode: 'less_than', value: 0.6 },
    ],
  },
}

export const keywordPerformanceBuild: SectionBuild = ({ tab, dropdownFilters, topTab }) => {
  const topTabData = KEYWORD_PERFORMANCE_TOP_TAB_DATA[topTab?.name ?? 'All'] ?? { filters: [] }
  return {
    id: `1-keyword-performance-${topTab?.name ?? 'All'}`,
    reportId: 71,
    scheduleTabs: ['overview'],
    dimensions: ['campaign_name', 'adgroup_name', 'keyword'],
    filters: [
      ...(tab.channel
        ? [{ field: 'marketing_channel', match_mode: 'equals', value: tab.channel } as ReportFilter]
        : []),
      ...dropdownFilters,
      ...topTabData.filters,
    ],
    ...(topTabData.sortField ? { sortField: topTabData.sortField } : {}),
    // The toggle's chart alternative: a keyword-level table can run to thousands of rows, so the
    // chart groups by campaign (the table's own top dimension) rather than by keyword — a
    // per-keyword bar chart would be unreadable. `column` accepts 1 dimension / up to 10 metrics
    // (`chartRendererRegistry.ts`); cost + clicks are the two metrics every search-ads schedule on
    // this partition carries (they are already columns in the table itself).
    allowedViews: ['table', 'chart'],
    chartType: 'column',
    chartDimensions: ['campaign_name'],
    chartMetrics: ['cost', 'clicks'],
  }
}

// ── Quality Score Elements (per-tab chart specs as a local map) ────────────────────────
export const QUALITY_SCORE_TAB_DATA: Record<
  string,
  { chartType: 'stacked_bar' | 'heatmap'; chartDimensions: string[]; filters: ReportFilter[] }
> = {
  'Keyword Match Type': {
    chartType: 'stacked_bar',
    chartDimensions: ['keyword_match_type'],
    filters: [{ field: 'keyword_match_type', match_mode: 'not_in', value: ['(empty)'] }],
  },
  'Ad Relevance Distribution': {
    // v3 heatmap fieldValues (dimension_x/dimension_y/metric) → role order of the dimensions.
    chartType: 'heatmap',
    chartDimensions: ['ad_relevance', 'landing_page_quality_score'],
    filters: [{ field: 'ad_relevance', match_mode: 'not_in', value: ['-'] }],
  },
}

export const qualityScoreElementsBuild: SectionBuild = ({ tab, dropdownFilters }) => {
  const data = QUALITY_SCORE_TAB_DATA[tab.name] ?? QUALITY_SCORE_TAB_DATA['Keyword Match Type']!
  return {
    id: `71-quality-score-elements-${data.chartType}`,
    reportId: 71,
    scheduleTabs: ['google_ads_keyword'],
    view: 'chart',
    // Not `chart-table` composition (both at once) — a plain toggle. Table mirrors the chart's
    // own dimensions/metric rather than falling back to the schedule's full column set, so the
    // two views stay the same breakdown, just plotted differently.
    allowedViews: ['table', 'chart'],
    dimensions: data.chartDimensions,
    metrics: ['cost'],
    chartType: data.chartType,
    chartDimensions: data.chartDimensions,
    chartMetrics: ['cost'],
    filters: [
      ...data.filters,
      ...dropdownFilters,
      { field: 'marketing_channel', match_mode: 'equals', value: 'google_ads' },
    ],
  }
}

// ── Keyword Labels (v3 getSearchLabelConfig: per-tab label column, chart-only) ─────────
export const KEYWORD_LABEL_COLS: Record<string, string> = {
  Category: 'category_label',
  Brand: 'brand_label',
  Color: 'color_label',
}

export const keywordLabelsBuild: SectionBuild = ({ tab, dropdownFilters }) => {
  const labelCol = KEYWORD_LABEL_COLS[tab.name] ?? 'category_label'
  return {
    id: `71-${tab.name}`,
    reportId: 71,
    scheduleTabs: ['overview'],
    view: 'chart',
    allowedViews: ['table', 'chart'],
    dimensions: [labelCol],
    metrics: ['cost', 'conversions', 'conversions_value'],
    chartType: 'column',
    chartDimensions: [labelCol],
    chartMetrics: ['cost', 'conversions', 'conversions_value'],
    sortField: 'cost',
    filters: [...dropdownFilters, { field: labelCol, match_mode: 'regex', value: '.*[A-Za-z].*r' }],
  }
}

// ── Search Terms (v3 SEARCH_TERM_PLATFORM_TABS request data as a local map) ────────────
export const SEARCH_TERM_TOP_TABS: SectionTopTab[] = [
  { name: 'All Search Terms', labelKey: 'reports.searchAdsExcellence.topTabs.allSearchTerms' },
  { name: 'High CPC - No Conversion', labelKey: 'reports.searchAdsExcellence.topTabs.highCpc' },
  { name: 'Duplicate Queries', labelKey: 'reports.searchAdsExcellence.topTabs.duplicateQueries' },
]

export const SEARCH_TERM_TOP_TAB_DATA: Record<
  string,
  {
    sortField?: string
    filters: ReportFilter[]
    breakdown?: string
    countUniqueDimensions?: string[]
  }
> = {
  'All Search Terms': { filters: [] },
  'High CPC - No Conversion': {
    sortField: 'cpc',
    filters: [{ field: 'conversions', match_mode: 'less_than', value: 0.2 }],
  },
  'Duplicate Queries': {
    // v3 also hid keyword/campaign/adgroup columns here — waits for ORP-18643 column shaping.
    breakdown: 'adgroup_id',
    countUniqueDimensions: ['adgroup_id'],
    filters: [
      { field: 'count_unique_adgroup_id', match_mode: 'greater_than', value: 1 },
      { field: 'search_term', match_mode: 'not_equals', value: 'other' },
      { field: 'clicks', match_mode: 'greater_than', value: 0 },
    ],
  },
}

export const searchTermsBuild: SectionBuild = ({ tab, dropdownFilters, topTab }) => {
  const topTabData = SEARCH_TERM_TOP_TAB_DATA[topTab?.name ?? 'All Search Terms'] ?? {
    filters: [],
  }
  return {
    id: `5-search-terms-${topTab?.name ?? 'All Search Terms'}-${tab.name}`,
    reportId: 5,
    scheduleTabs: tab.tabs ?? [],
    partitionOnly: true,
    filters: [
      ...(tab.channel
        ? [{ field: 'marketing_channel', match_mode: 'equals', value: tab.channel } as ReportFilter]
        : []),
      ...dropdownFilters,
      ...topTabData.filters,
    ],
    ...(topTabData.sortField ? { sortField: topTabData.sortField } : {}),
    ...(topTabData.breakdown ? { breakdown: topTabData.breakdown } : {}),
    ...(topTabData.countUniqueDimensions
      ? { countUniqueDimensions: topTabData.countUniqueDimensions }
      : {}),
  }
}

// ── Extensions (v3 getExtensionConfig) ─────────────────────────────────────────────────
export const extensionsBuild: SectionBuild = ({ tab }) => ({
  id: `1-extension-${tab.name}`,
  reportId: 1,
  scheduleTabs: tab.tabs ?? [],
  filters: [
    {
      field: 'asset_type',
      match_mode: 'equals',
      value: tab.name === 'Callout' ? 'CALLOUT' : 'SITELINK',
    },
  ],
})

// ── Organic Keywords (v3 getOrganicKeywordsConfig) ─────────────────────────────────────
export const ORGANIC_TAB_FILTERS: Record<string, ReportFilter[]> = {
  'Short Tail Keywords': [{ field: 'keyword', match_mode: 'not_regex', value: '(?:.*\\s){3}' }],
  'Long Tail Keywords': [{ field: 'keyword', match_mode: 'regex', value: '(?:.*\\s){3}' }],
}

export const organicKeywordsBuild: SectionBuild = ({ tab, dropdownFilters }) => ({
  id: `5-overview-${tab.name}`,
  reportId: 5,
  scheduleTabs: tab.tabs ?? [],
  sortField: 'organic_clicks',
  // v3: the Position tab applies dropdowns only; other tabs add their regex defaults.
  filters: [...dropdownFilters, ...(ORGANIC_TAB_FILTERS[tab.name] ?? [])],
})

export const paidOrganicBuild: SectionBuild = () => ({
  id: '5-paid_organic_term',
  reportId: 5,
  scheduleTabs: ['overview'],
})

// ── Dashboard trendlines (v3 `dashboardConfig` → module 09 DashboardChartArea) ─────────
export const campaignOverviewBuild: SectionBuild = ({ dropdownFilters }) => ({
  id: '71-campaign-overview',
  reportId: 71,
  scheduleTabs: ['overview'],
  filters: [
    ...dropdownFilters,
    { field: 'marketing_channel', match_mode: 'equals', value: 'google_ads' },
  ],
})

export const organicTrendlineBuild: SectionBuild = ({ dropdownFilters }) => ({
  id: '5-organic-keywords-dashboard',
  reportId: 5,
  scheduleTabs: ['False_branded_keyword'],
  filters: dropdownFilters,
})

// ── Keyword Quality Scores (v3 `scatterJitter` → renderable `scatter`; table added alongside
// the chart per the same table+chart toggle rule as the rest of this layout) ──────────────
export const keywordQualityScoresBuild: SectionBuild = ({ dropdownFilters }) => ({
  id: '71-keyword-quality-scores',
  reportId: 71,
  scheduleTabs: ['google_ads_keyword'],
  view: 'chart',
  allowedViews: ['table', 'chart'],
  dimensions: ['keyword'],
  metrics: ['clicks', 'quality_score'],
  chartType: 'scatter',
  chartDimensions: ['keyword'],
  chartMetrics: ['clicks', 'quality_score'],
  filters: [
    { field: 'clicks', match_mode: 'greater_than', value: 10 },
    { field: 'marketing_channel', match_mode: 'equals', value: 'google_ads' },
    ...dropdownFilters,
  ],
})
